import innosearchBallSvg from '@/assets/icons/innosearch-ball.svg'
import {activeThreadIdAtom, latestPromptsAtom} from '@/components/ChatSection/states'
import MiniChat, {type MiniChatControl} from '@/components/MiniChat/MiniChat'
import Sheet from '@/components/Sheet/Sheet'
import type {Thread} from '@/models/threadModel'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import useClientValue from '@/utils/hooks/useClientValue'
import useIsChanged from '@/utils/hooks/useIsChanged'
import {useIsMobile} from '@/utils/hooks/useWindowSize'

import style from './AskInnoSearch.module.scss'
export interface AskInnoSearchControl {
  ask: MemoizedCallback<(e: string) => void>
  updateVariationThreadId: MemoizedCallback<(threadId: string | undefined) => void>
}

interface Props {
  threadId: Thread['id']
  prompts?: string[]
  ref?: Ref<AskInnoSearchControl>
  containerRef?: Ref<HTMLDivElement>
}

const AskInnoSearch = memo(function AskInnoSearch({containerRef, prompts, ref, threadId}: Props) {
  const setActiveThreadId = useSetAtom(activeThreadIdAtom)
  setActiveThreadId(threadId)

  const isMobile = useIsMobile()

  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const sheetOpenIs = useLatest(isSheetOpen)

  let latestPrompts = useAtomValue(latestPromptsAtom)
  const setLatestPrompts = useSetAtom(latestPromptsAtom)

  const isPromptsChanged = useIsChanged(prompts)

  // If prompts are updated, update latestPrompts accordingly
  if (isPromptsChanged && prompts && prompts.length > 0) {
    // TODO: find out why latestPrompts is not being updated by jotai
    setLatestPrompts(prompts)
    // And we have to update latestPrompts manually
    latestPrompts = prompts
  }

  const miniChatRef = useRef<MiniChatControl>(null)
  const nextQuestion = useRef('')
  const nextVariationThreadId = useRef<string>(undefined)

  const ask = useCallback((q?: string) => {
    if (!sheetOpenIs.current) {
      if (q) nextQuestion.current = q
      setIsSheetOpen(true)
    } else if (q) miniChatRef.current?.submitMessage(q)
  }, [])

  const updateVariationThreadId = useCallback((threadId: string | undefined) => {
    nextVariationThreadId.current = threadId
  }, [])

  const onSheetPresentChange = useCallback((isPresent: boolean) => {
    if (!isPresent) return

    miniChatRef.current?.setVariationThreadId(nextVariationThreadId.current)

    if (nextQuestion.current) {
      const q = nextQuestion.current
      nextQuestion.current = ''
      miniChatRef.current?.submitMessage(q)
    }
  }, [])

  const onClose = useCallback(() => {
    setIsSheetOpen(false)
  }, [])

  const sheetMode = isMobile ? 'bottom' : 'right'
  const sheetSize = useClientValue(() => {
    if (!isMobile) {
      return Math.min((window.innerWidth * 50) / 100, 500)
    }
    return (window.innerHeight * 90) / 100
  }, 0)

  useImperativeHandle(ref, () => {
    return {
      ask,
      updateVariationThreadId,
    }
  }, [ask, updateVariationThreadId])

  return (
    <>
      <section
        ref={containerRef}
        className={style.PromptsContainer}
        aria-label='Ask Innosearch'
        {...skipTargetProps('ask-innosearch')}
      >
        <div className={style.PromptsWrapper}>
          <button
            className={style.Ask}
            onClick={() => {
              ask()
            }}
          >
            <div className={style.AskIcon}>
              <img src={innosearchBallSvg} alt='' />
            </div>
            <div className={style.Divider} />
            <div>
              Ask Innosearch<span aria-hidden>...</span>
            </div>
          </button>
          {/* Hide suggestion https://innosearch.atlassian.net/browse/IN-602 */}
          {/* {!isMobile && latestPrompts.length > 0 && (
              <ul className={style.Prompts}>
                {latestPrompts.slice(0, 6).map(prompt => (
                  <li key={prompt}>
                    <button
                      className={style.Prompt}
                      onClick={() => {
                        ask(prompt)
                      }}
                      aria-haspopup='dialog'
                    >
                      {prompt}
                    </button>
                  </li>
                ))}
              </ul>
            )} */}
        </div>
      </section>
      <Sheet
        onPresentChange={onSheetPresentChange}
        mode={sheetMode}
        isOpen={isSheetOpen}
        onClose={onClose}
        size={sheetSize}
      >
        <MiniChat ref={miniChatRef} onClose={onClose}>
          <ul className={style.Prompts}>
            {latestPrompts.slice(0, 6).map(prompt => (
              <li key={prompt}>
                <button
                  className={style.Prompt}
                  onClick={() => {
                    ask(prompt)
                  }}
                  aria-haspopup='dialog'
                >
                  {prompt}
                </button>
              </li>
            ))}
          </ul>
        </MiniChat>
      </Sheet>
    </>
  )
})

export default AskInnoSearch
